<template>
  <div class="stripe-payment">
    <div
      class="payment-element"
      ref="element"
    />
    <SubmitButton @submitted="processDonation" />
  </div>
</template>

<script>
import { handleError, handleSuccess } from '@/lib/payment/handlers'
import { useDonationStore } from '@/stores/donation'
import { usePaymentStore } from '@/stores/payment'
import { useRootStore } from '@/stores/root'
import { v4 as uuid } from 'uuid'
import { verifyPublicKey } from './methods'
import axios from 'axios'
import SubmitButton from '@/components/form/SubmitButton'

export default {
  name: 'StripeComponent',
  components: { SubmitButton },
  setup () {
    const donationStore = useDonationStore()
    const paymentStore = usePaymentStore()
    const rootStore = useRootStore()
    const stripe = null
    const elements = null
    return { rootStore, donationStore, paymentStore, stripe, elements }
  },
  computed: {
    thankYouPath () { return this.$t('routeNames.thankYou') }
  },
  methods: {
    async processDonation () {
      this.$scrollToElement('.column')
      const { error: submitError } = await this.elements.submit()
      if (submitError) {
        handleError(this, submitError); return;
      }
      const elements = this.elements

      const params = {
        ...this.donationStore.donationParams,
        payment: { adapter: 'stripe', reference: this.donationStore.reference },
        location: this.$l10n.currentLocation.key,
        language: this.$l10n.currentLanguage,
        turnstile_response: this.rootStore.turnstileToken
      }
      let donationResponse = null
      try {
        donationResponse = await axios.post(`${process.env.VUE_APP_BACKEND_HOST}/donation`, params)
      } catch (error) {
        const failureMessage = error.response ? error.response.data : error
        const errMessage = (('message' in failureMessage) ? failureMessage.message : failureMessage)
        handleError(this, errMessage)
        return
      }

      const clientSecret = donationResponse.data.client_secret
      const reference = donationResponse.data.reference
      const returnUrl = `${location.origin}/callbacks/stripe/authorised?l=${this.$l10n.currentLocation.key}&reference=${reference}`

      let confirmError, paymentIntent
      try {
        ({ error: confirmError, paymentIntent } = await this.stripe.confirmPayment({
          elements,
          clientSecret,
          confirmParams: {
            return_url: returnUrl
          },
          redirect: 'if_required'
        }))
      } catch (err) {
        try { await axios.patch(`${process.env.VUE_APP_BACKEND_HOST}/donation/${reference}/failure`) } catch (_) { /* best effort */ }
        handleError(this, err)
        return
      }

      if (confirmError) {
        try { await axios.patch(`${process.env.VUE_APP_BACKEND_HOST}/donation/${reference}/failure`) } catch (_) { /* best effort */ }
        handleError(this, confirmError)
        return
      }

      if (paymentIntent.status !== 'succeeded') {
        try { await axios.patch(`${process.env.VUE_APP_BACKEND_HOST}/donation/${reference}/failure`) } catch (_) { /* best effort */ }
        handleError(this, new Error(this.$t('paymentStatus.paymentIncomplete')))
        return
      }

      try {
        await axios.post(`${process.env.VUE_APP_BACKEND_HOST}/donation/${reference}/confirm`)
      } catch (error) {
        const failureMessage = error.response ? error.response.data : error
        const errMessage = (('message' in failureMessage) ? failureMessage.message : failureMessage)
        handleError(this, errMessage)
        return
      }
      handleSuccess(this, this.thankYouPath)
    }
  },
  mounted () {
    const config = this.$l10n.paymentMethods.stripe
    const apiKey = process.env[config.apiKey]
    this.donationStore.setReference(uuid())
    this.paymentStore.setComplete(false)
    this.$loadScript('https://js.stripe.com/v3/')
      .then(() => {
        this.stripe = new Stripe(process.env[config.apiKey], { locale: config.locale })
        verifyPublicKey(this.stripe, apiKey);
        return this.stripe
      })
      .then(async () => {
        const amountInCents = this.$l10n.amountInCents(this.donationStore.amount, this.donationStore.currency)
        const options = {
          currency: this.donationStore.currency,
          amount: amountInCents,
          appearance: { theme: 'flat' },
          layout: { type: 'accordion', defaultCollapsed: false, radios: false, spacedAccordionItems: true }
        };
        const elementsMode = (this.donationStore.type === 'oneOff') ? 'payment' : 'subscription'
        const elements = this.stripe.elements({ mode: elementsMode, ...options })
        this.elements = elements
        const paymentElement = elements.create('payment', {
          defaultValues: {
            billingDetails: { address: { postal_code: this.donationStore.zip, country: this.donationStore.country } }
          }
        });
        paymentElement.mount(this.$refs.element)
        paymentElement.on('change', async ({ complete }) => { this.paymentStore.setComplete(complete) })
      })
  }
}
</script>

<style scoped>
  .payment-element {
    background-color: white;
    padding: 1rem;
  }
</style>
